import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaChevronRight, FaChevronDown, FaPencilAlt, FaCheck, FaPlus, FaTrash } from 'react-icons/fa';

const WikiTreeView = ({
    pages,
    selectedPageId,
    onSelect,
    onReorder,
    onAddChild,
    onDelete,
    showDraftIndicators = false,
    storageKey = 'wikiTreeExpanded'
}) => {
    const [expandedNodes, setExpandedNodes] = useState(() => {
        const saved = localStorage.getItem(storageKey);
        return saved ? new Set(JSON.parse(saved)) : new Set();
    });
    const [contextMenu, setContextMenu] = useState(null);
    const [draggedPageId, setDraggedPageId] = useState(null);
    const [dragOverPageId, setDragOverPageId] = useState(null);
    const [dropPosition, setDropPosition] = useState(null);
    const contextMenuRef = useRef(null);

    useEffect(() => {
        localStorage.setItem(storageKey, JSON.stringify([...expandedNodes]));
    }, [expandedNodes, storageKey]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (contextMenuRef.current && !contextMenuRef.current.contains(event.target)) {
                setContextMenu(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const buildTree = useCallback((flatPages) => {
        if (!flatPages || flatPages.length === 0) return [];

        const pageMap = new Map();
        flatPages.forEach(page => {
            pageMap.set(page.pageId, { ...page, children: [] });
        });

        const rootPages = [];
        flatPages.forEach(page => {
            const node = pageMap.get(page.pageId);
            if (!page.parentId) {
                rootPages.push(node);
            } else {
                const parent = pageMap.get(page.parentId);
                if (parent) {
                    parent.children.push(node);
                } else {
                    rootPages.push(node);
                }
            }
        });

        const sortByOrder = (nodes) => {
            nodes.sort((a, b) => {
                if (a.order === undefined || b.order === undefined) {
                    console.error('WikiTreeView: Page missing order field', { a: a.pageId, b: b.pageId });
                }
                return a.order - b.order;
            });
            nodes.forEach(node => sortByOrder(node.children));
        };
        sortByOrder(rootPages);

        return rootPages;
    }, []);

    const tree = buildTree(pages);

    const toggleExpand = (pageId, event) => {
        event.stopPropagation();
        setExpandedNodes(prev => {
            const newSet = new Set(prev);
            if (newSet.has(pageId)) {
                newSet.delete(pageId);
            } else {
                newSet.add(pageId);
            }
            return newSet;
        });
    };

    const handleSelect = (pageId) => {
        if (onSelect) {
            onSelect(pageId);
        }
        setContextMenu(null);
    };

    const handleContextMenu = (event, pageId) => {
        event.preventDefault();
        event.stopPropagation();
        setContextMenu({
            x: event.clientX,
            y: event.clientY,
            pageId
        });
    };

    const handleAddChild = () => {
        if (contextMenu && onAddChild) {
            onAddChild(contextMenu.pageId);
            if (!expandedNodes.has(contextMenu.pageId)) {
                setExpandedNodes(prev => new Set([...prev, contextMenu.pageId]));
            }
        }
        setContextMenu(null);
    };

    const handleDelete = () => {
        if (contextMenu && onDelete) {
            onDelete(contextMenu.pageId);
        }
        setContextMenu(null);
    };

    const handleDragStart = (event, pageId) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', pageId);
        setDraggedPageId(pageId);
    };

    const handleDragOver = (event, pageId) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';

        if (draggedPageId === pageId) return;

        const rect = event.currentTarget.getBoundingClientRect();
        const y = event.clientY - rect.top;
        const height = rect.height;

        let position;
        if (y < height * 0.25) {
            position = 'before';
        } else if (y > height * 0.75) {
            position = 'after';
        } else {
            position = 'inside';
        }

        setDragOverPageId(pageId);
        setDropPosition(position);
    };

    const handleDragLeave = () => {
        setDragOverPageId(null);
        setDropPosition(null);
    };

    const handleDrop = (event, targetPageId) => {
        event.preventDefault();
        const draggedId = event.dataTransfer.getData('text/plain');

        if (!draggedId || draggedId === targetPageId || !onReorder) {
            setDraggedPageId(null);
            setDragOverPageId(null);
            setDropPosition(null);
            return;
        }

        const targetPage = pages.find(p => p.pageId === targetPageId);
        if (!targetPage) {
            setDraggedPageId(null);
            setDragOverPageId(null);
            setDropPosition(null);
            return;
        }

        let newParentId;
        let newOrder;

        if (dropPosition === 'inside') {
            newParentId = targetPageId;
            const siblings = pages.filter(p => p.parentId === targetPageId);
            if (siblings.length > 0) {
                const siblingWithMissingOrder = siblings.find(s => s.order === undefined);
                if (siblingWithMissingOrder) {
                    console.error('WikiTreeView: Sibling missing order field', { pageId: siblingWithMissingOrder.pageId });
                }
                newOrder = Math.max(...siblings.map(s => s.order)) + 1;
            } else {
                newOrder = 1;
            }
            if (!expandedNodes.has(targetPageId)) {
                setExpandedNodes(prev => new Set([...prev, targetPageId]));
            }
        } else {
            newParentId = targetPage.parentId;
            const siblings = pages.filter(p => p.parentId === targetPage.parentId && p.pageId !== draggedId);
            const targetIndex = siblings.findIndex(p => p.pageId === targetPageId);

            if (dropPosition === 'before') {
                newOrder = targetIndex > 0 ? siblings[targetIndex - 1].order + 0.5 : targetPage.order - 0.5;
            } else {
                newOrder = targetIndex < siblings.length - 1 ? (targetPage.order + siblings[targetIndex + 1].order) / 2 : targetPage.order + 1;
            }
        }

        onReorder(draggedId, newParentId, newOrder);

        setDraggedPageId(null);
        setDragOverPageId(null);
        setDropPosition(null);
    };

    const handleDragEnd = () => {
        setDraggedPageId(null);
        setDragOverPageId(null);
        setDropPosition(null);
    };

    const isDraft = (page) => {
        if (!showDraftIndicators) return false;
        return !page.contentHtml || page.contentHtml !== page.draftHtml;
    };

    const renderNode = (node, depth = 0) => {
        const hasChildren = node.children && node.children.length > 0;
        const isExpanded = expandedNodes.has(node.pageId);
        const isSelected = selectedPageId === node.pageId;
        const isDragging = draggedPageId === node.pageId;
        const isDragOver = dragOverPageId === node.pageId;
        const pageIsDraft = isDraft(node);

        let dropIndicatorClass = '';
        if (isDragOver && dropPosition) {
            if (dropPosition === 'before') {
                dropIndicatorClass = 'border-t-2 border-primary-500';
            } else if (dropPosition === 'after') {
                dropIndicatorClass = 'border-b-2 border-primary-500';
            } else if (dropPosition === 'inside') {
                dropIndicatorClass = 'bg-primary-50 border-2 border-primary-300';
            }
        }

        return (
            <div key={node.pageId} className={isDragging ? 'opacity-50' : ''}>
                <div
                    className={`
                        flex items-center px-2 py-1.5 cursor-pointer rounded
                        hover:bg-gray-100
                        ${isSelected ? 'bg-primary-50 border-l-2 border-primary-500' : ''}
                        ${dropIndicatorClass}
                    `}
                    style={{ paddingLeft: `${depth * 16 + 8}px` }}
                    onClick={() => handleSelect(node.pageId)}
                    onContextMenu={(e) => handleContextMenu(e, node.pageId)}
                    draggable
                    onDragStart={(e) => handleDragStart(e, node.pageId)}
                    onDragOver={(e) => handleDragOver(e, node.pageId)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, node.pageId)}
                    onDragEnd={handleDragEnd}
                >
                    <span
                        className="w-5 h-5 flex items-center justify-center text-gray-400 flex-shrink-0"
                        onClick={(e) => hasChildren && toggleExpand(node.pageId, e)}
                    >
                        {hasChildren ? (
                            isExpanded ? (
                                <FaChevronDown className="w-3 h-3" />
                            ) : (
                                <FaChevronRight className="w-3 h-3" />
                            )
                        ) : null}
                    </span>
                    <span
                        className={`
                            flex-1 text-sm truncate ml-1
                            ${pageIsDraft ? 'text-gray-400 italic' : 'text-gray-700'}
                        `}
                    >
                        {node.title}
                    </span>
                    {/* The title sits on the SPAN, not the icon. react-icons turns a
                        `title` prop into a <title> child of the svg, which is a
                        different thing from the attribute NativeTooltips intercepts -
                        left on the icon it would still show the browser's own popup. */}
                    {showDraftIndicators && (
                        <span className="ml-2 flex-shrink-0" title={pageIsDraft ? 'Draft' : 'Published'}>
                            {pageIsDraft ? (
                                <FaPencilAlt className="w-3 h-3 text-gray-400" />
                            ) : (
                                <FaCheck className="w-3 h-3 text-green-500" />
                            )}
                        </span>
                    )}
                </div>
                {hasChildren && isExpanded && (
                    <div>
                        {node.children.map(child => renderNode(child, depth + 1))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="relative">
            <div className="space-y-0.5">
                {tree.length === 0 ? (
                    <p className="text-sm text-gray-500 italic px-2 py-4">No pages yet</p>
                ) : (
                    tree.map(node => renderNode(node, 0))
                )}
            </div>

            {contextMenu && (
                <div
                    ref={contextMenuRef}
                    className="fixed bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 min-w-40"
                    style={{ top: contextMenu.y, left: contextMenu.x }}
                >
                    {onAddChild && (
                        <button
                            className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                            onClick={handleAddChild}
                        >
                            <FaPlus className="w-3 h-3" />
                            Add child page
                        </button>
                    )}
                    {onDelete && (
                        <button
                            className="w-full px-4 py-2 text-sm text-left text-red-600 hover:bg-red-50 flex items-center gap-2"
                            onClick={handleDelete}
                        >
                            <FaTrash className="w-3 h-3" />
                            Delete
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default WikiTreeView;
